<template>
  <div class="referral-campaign-form">
    <div class="category-top-bar sticky-header">
      <div class="top-bar">
        <p>
          <a class="btn btn-orange btn-icon back" :title="$t('general.back')" @click.prevent="backButton">
            <i class="ti-arrow-left"></i>
            {{ $t('general.back') }}
          </a>
        </p>
        <div class="text-right">
          <p class="d-inline-flex">
            <a class="btn btn-danger btn-icon delete mar-rt-10" v-if="campaignId" @click.prevent="handleDelete">
              <img src="/static/img/icons/icon-close.png">
              {{ $t('general.delete') }}
            </a>
            <button type="submit" :disabled="loading || saving" @click.prevent="save" class="btn btn-icon btn-success btn mar-rt-10 save">
              <img src="/static/img/icons/save.png">
              {{ $t('general.save') }}
            </button>
          </p>
        </div>
      </div>
    </div>

    <div :class="(loading ? 'show-loader' : 'hide-loader') + ' referral-campaign-form__loader'">
      <div class="el-loading-spinner">
        <svg viewBox="25 25 50 50" class="circular">
          <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
        </svg>
      </div>
    </div>

    <div class="card mar-to-15">
      <div class="card-content">
        <h5 class="referral-card-title">{{ $t('referralProgram.generalSettings') }}</h5>
        <div class="row">
          <div class="col-md-6">
            <div class="form-group" :class="{ 'has-error': fieldErrors.name }">
              <label :class="{ 'text-danger': fieldErrors.name }">{{ $t('referralProgram.campaignName') }}</label>
              <input
                v-model="model.name"
                type="text"
                class="form-control"
                :class="{ error: fieldErrors.name }"
                name="referral_campaign_name"
                @input="clearFieldError('name')"
              />
              <small class="text-danger" v-if="fieldErrors.name">{{ $t('referralProgram.nameRequired') }}</small>
            </div>
            <div class="form-group referral-form-switches-row">
              <div class="referral-form-switch-item">
                <p-switch v-model="model.active" type="success"></p-switch>
                <label>{{ $t('referralProgram.active') }}</label>
              </div>
              <div class="referral-form-switch-item">
                <p-switch v-model="model.is_visible_for_users" type="success"></p-switch>
                <label>{{ $t('referralProgram.v2.visibleInApp') }}</label>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>{{ $t('referralProgram.rewardType') }}</label>
              <select v-model="rewardType" class="form-control">
                <option value="LOYALTY_POINTS" :disabled="!loyaltyPointsEnabled">{{ $t('referralProgram.rewardTypeLoyaltyPoints') }}</option>
                <option value="WALLET">{{ $t('referralProgram.rewardTypeWallet') }}</option>
              </select>
              <small v-if="!loyaltyPointsEnabled">
                <i class="fa fa-info-circle" aria-hidden="true"></i>
                {{ $t('referralProgram.loyaltyPointsModuleRequired') }}
                <a href="#" @click.prevent="goToLoyaltySettings">{{ $t('referralProgram.loyaltyPointsModuleLink') }}</a>
              </small>
            </div>
            <div class="form-group">
              <label>{{ $t('referralProgram.availableBetween') }}</label>
              <el-date-picker
                v-model="availabilityRange"
                type="daterange"
                value-format="yyyy-MM-dd"
                format="dd/MM/yyyy"
                style="width:100%"
              />
              <small class="text-muted">{{ $t('referralProgram.optionalAvailability') }}</small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card mar-to-15">
      <div class="card-content">
        <h5 class="referral-card-title">{{ $t('referralProgram.v2.hierarchyTitle') }}</h5>
        <p>{{ $t('referralProgram.v2.hierarchy') }}</p>
        <p class="text-muted">{{ $t('referralProgram.v2.purchaseFlow') }}</p>
      </div>
    </div>

    <div class="card mar-to-15">
      <div class="card-content">
        <div class="referral-section-heading referral-form-inline-toggle">
          <h5 class="referral-section-heading__title">{{ $t('referralProgram.orderRewardsSection') }}</h5>
          <p-switch v-model="model.order_rewards_active" type="success"></p-switch>
        </div>
        <div v-show="model.order_rewards_active">
          <div class="form-group" :class="{ 'has-error': fieldErrors.maxOrders }">
            <label for="referral-max-orders">{{ $t('referralProgram.maxOrdersForOrderRewards') }}</label>
            <input id="referral-max-orders" v-model="maxOrdersInput" type="number" min="1" step="1" class="form-control" :placeholder="$t('referralProgram.unlimitedPlaceholder')" @input="clearFieldError('maxOrders')" />
            <small v-if="fieldErrors.maxOrders" class="text-danger">{{ $t('referralProgram.v2.maxOrdersInvalid') }}</small>
          </div>
          <div class="row">
            <referral-reward-rule v-for="role in roles" :key="role" v-model="rewardSettings[role].order_reward" column-class="col-md-6"
              :field-id="role + '-order'" :title="$t('referralProgram.v2.' + role)" :coupons="coupons" :coupons-loading="couponsLoading"
              :loyalty-enabled="loyaltyPointsEnabled" :default-reward-type="defaultRewardType" :error="fieldErrors[role + '.order_reward'] || ''"
              @input="clearFieldError(role + '.order_reward')" />
          </div>
        </div>
        <p class="referral-reward-summary">{{ orderRewardsSummaryText }}</p>
      </div>
    </div>

    <div class="card mar-to-15">
      <div class="card-content">
        <div class="referral-section-heading referral-form-inline-toggle">
          <div class="referral-section-heading__text">
            <h5 class="referral-section-heading__title">{{ $t('referralProgram.v2.cashbackTitle') }}</h5>
            <small class="text-muted">{{ $t('referralProgram.v2.cashbackHelp') }}</small>
          </div>
          <p-switch v-model="cashbackEnabled" type="success"></p-switch>
        </div>
        <div v-show="cashbackEnabled" class="row">
          <referral-reward-rule v-for="role in cashbackRoles" :key="role" v-model="rewardSettings[role].cashback" column-class="col-md-12"
            :allow-coupons="false" :field-id="role + '-cashback'" :title="$t('referralProgram.v2.' + role)" :coupons="coupons" :coupons-loading="couponsLoading"
            :loyalty-enabled="loyaltyPointsEnabled" :default-reward-type="defaultRewardType" :error="fieldErrors[role + '.cashback'] || ''"
            @input="clearFieldError(role + '.cashback')" />
        </div>
        <p class="referral-reward-summary">{{ cashbackSummaryText }}</p>
      </div>
    </div>
    <p v-if="couponsLoadFailed" class="text-danger" role="alert">
      {{ $t('referralProgram.v2.couponsLoadFailed') }}
      <button type="button" class="btn btn-sm btn-default" @click="loadCoupons">{{ $t('referralProgram.v2.retry') }}</button>
    </p>
  </div>
</template>

<script>
import Vue from 'vue'
import swal from 'sweetalert2'
import { DatePicker } from 'element-ui'
import PSwitch from 'src/components/UIComponents/Switch.vue'
import ReferralRewardRule from './ReferralRewardRule.vue'
import { attemptReferralCampaignDelete } from './referralCampaignDelete'
import { rewardRoles, cashbackRoles, rewardAspectsForRole, hydrateRewardSettings, serializeRewardSettings, validateRewardSettings, summarizeRewards } from './referralRewardSettings'

Vue.use(DatePicker)

export default {
  components: { [DatePicker.name]: DatePicker, PSwitch, ReferralRewardRule },
  props: { route: { type: Object, default: () => ({}) } },
  data () {
    return {
      fieldErrors: {},
      loading: false,
      saving: false,
      loyaltyInUse: false,
      campaignId: null,
      availabilityRange: null,
      maxOrdersInput: '',
      rewardType: 'LOYALTY_POINTS',
      roles: rewardRoles,
      cashbackRoles,
      rewardSettings: hydrateRewardSettings(),
      cashbackEnabled: false,
      coupons: [],
      couponsLoading: false,
      couponsLoadFailed: false,
      model: { name: '', active: true, is_visible_for_users: false, starts_at: null, ends_at: null, order_rewards_active: false }
    }
  },
  computed: {
    loyaltyPointsEnabled () { return this.loyaltyInUse },
    defaultRewardType () { return this.rewardType === 'WALLET' ? 2 : 1 },
    campaignIdFromRoute () {
      const fromProp = this.route && this.route.params && this.route.params.id
      const fromRouter = this.$route && this.$route.params && this.$route.params.id
      const id = fromProp != null && fromProp !== '' ? fromProp : fromRouter
      return id != null && id !== '' ? id : null
    },
    orderRewardsSummaryText () {
      return this.rewardSummary('order_reward')
    },
    cashbackSummaryText () {
      return this.cashbackEnabled ? this.rewardSummary('cashback') : this.$t('referralProgram.rewardSummaryDisabled')
    }
  },
  created () {
    this.syncLoyaltyInUse()
    this.loadCoupons()
    this.$bus.$on('company-data-updated', this.onCompanyDataUpdated)
  },
  beforeDestroy () {
    this.$bus.$off('company-data-updated', this.onCompanyDataUpdated)
  },
  watch: {
    campaignIdFromRoute: {
      immediate: true,
      handler (id) {
        this.campaignId = id
        if (id != null) this.loadCampaign()
      }
    },
    availabilityRange (val) {
      this.model.starts_at = val && val.length === 2 ? val[0] : null
      this.model.ends_at = val && val.length === 2 ? val[1] : null
    }
  },
  methods: {
    rewardSummary (aspect) {
      return summarizeRewards({ reward_type: this.defaultRewardType,
        reward_settings: this.rewardSettings,
        order_rewards_active: this.model.order_rewards_active }, aspect, this.$t.bind(this), this.coupons)
    },
    clearFieldError (field) { this.$delete(this.fieldErrors, field) },
    backButton () {
      this.$bus.$emit('refresh')
      this.$bus.$emit('close-modal')
    },
    goToLoyaltySettings () {
      this.$bus.$emit('close-modal')
      // Loyalty tab in MarketingSidebar (tabFlags index of 'isLoyality'; shifts when a tab is added above it)
      this.$nextTick(() => { this.$router.push({ path: '/marketing', query: { tabIndex: 15 } }).catch(() => {}) })
    },
    syncLoyaltyInUse (companyData) {
      const data = companyData || this.$session.get('selectedCompanyData') || {}
      this.loyaltyInUse = data.loyalty_in_use === true || data.loyalty_in_use === 1 || data.loyalty_in_use === '1' || data.loyalty_in_use === 'true'
      if (!this.campaignId && !this.loyaltyInUse) this.rewardType = 'WALLET'
    },
    onCompanyDataUpdated (companyData) {
      if (companyData && Number(companyData.id) === Number(this.$session.get('selectedCompany'))) this.syncLoyaltyInUse(companyData)
    },
    sessionPayload () {
      return { token: this.$session.get('token'), company_id: this.$session.get('selectedCompany') }
    },
    loadCoupons () {
      this.couponsLoading = true
      this.couponsLoadFailed = false
      this.apiRequest('/referral-campaign/coupon-options', 'GET', this.sessionPayload(), response => {
        this.couponsLoading = false
        if (response.status) {
          const data = response.data && response.data.data ? response.data.data : response.data
          this.coupons = Array.isArray(data) ? data : []
        } else {
          this.couponsLoadFailed = true
        }
      })
    },
    payload () {
      return Object.assign({}, this.sessionPayload(), {
        name: this.model.name.trim(),
        active: this.model.active,
        is_visible_for_users: this.model.is_visible_for_users,
        order_rewards_active: this.model.order_rewards_active,
        max_orders_for_order_rewards: String(this.maxOrdersInput).trim() === '' ? null : Number(this.maxOrdersInput),
        reward_type: this.defaultRewardType,
        reward_settings: serializeRewardSettings(this.rewardSettings, this.cashbackEnabled),
        datetime_validfrom: this.model.starts_at ? this.model.starts_at + ' 00:00:00' : null,
        datetime_validto: this.model.ends_at ? this.model.ends_at + ' 23:59:59' : null
      })
    },
    applyRecord (row) {
      const isEnabled = value => value === true || value === 1 || value === '1'
      this.model.name = row.name || ''
      this.model.active = row.active == null ? true : isEnabled(row.active)
      this.model.is_visible_for_users = isEnabled(row.is_visible_for_users)
      this.model.order_rewards_active = isEnabled(row.order_rewards_active)
      this.model.starts_at = row.datetime_validfrom ? String(row.datetime_validfrom).slice(0, 10) : null
      this.model.ends_at = row.datetime_validto ? String(row.datetime_validto).slice(0, 10) : null
      // Do not clear a one-sided availability bound when loading an existing campaign.
      if (this.model.starts_at && this.model.ends_at) this.availabilityRange = [this.model.starts_at, this.model.ends_at]
      this.maxOrdersInput = row.max_orders_for_order_rewards == null || Number(row.max_orders_for_order_rewards) <= 0 ? '' : String(row.max_orders_for_order_rewards)
      this.rewardType = Number(row.reward_type) === 2 || String(row.reward_type).toUpperCase() === 'WALLET' ? 'WALLET' : 'LOYALTY_POINTS'
      this.rewardSettings = hydrateRewardSettings(row)
      this.cashbackEnabled = this.cashbackRoles.some(role => this.rewardSettings[role].cashback.type !== 'NONE')
    },
    loadCampaign () {
      this.loading = true
      this.apiRequest('/referral-campaign/' + this.campaignId, 'GET', this.sessionPayload(), response => {
        this.loading = false
        if (response.status && response.data) {
          this.applyRecord(response.data.data || response.data)
        } else {
          swal('', this.$t('validations.wentWrong'), 'error')
        }
      })
    },
    validate () {
      const errors = validateRewardSettings(this.rewardSettings, this.model.order_rewards_active, this.cashbackEnabled)
      if (!this.model.name.trim()) errors.name = true
      if (String(this.maxOrdersInput).trim() !== '' && (!Number.isInteger(Number(this.maxOrdersInput)) || Number(this.maxOrdersInput) < 1)) errors.maxOrders = true
      this.roles.forEach(role => {
        rewardAspectsForRole(role).forEach(aspect => {
          if (!(aspect === 'order_reward' ? this.model.order_rewards_active : this.cashbackEnabled)) return
          const rule = this.rewardSettings[role][aspect]
          if (rule.type === 'COUPON' && !this.couponsLoading && !this.couponsLoadFailed && !this.coupons.some(coupon => Number(coupon.id) === Number(rule.coupon_id))) {
            errors[role + '.' + aspect] = 'couponUnavailable'
          }
        })
      })
      this.fieldErrors = errors
      return Object.keys(errors).length === 0
    },
    save () {
      if (this.loading || this.saving || !this.validate()) return
      this.saving = true
      this.apiRequest('/referral-campaign' + (this.campaignId ? '/' + this.campaignId : ''), this.campaignId ? 'PUT' : 'POST', this.payload(), response => {
        this.saving = false
        if (response.status) {
          swal(this.$t('general.success') + '!', '', 'success').catch(() => {})
          this.backButton()
        } else {
          const errors = response.errors || {}
          const messages = Object.keys(errors).map(key => Array.isArray(errors[key]) ? errors[key].join(', ') : errors[key]).filter(value => typeof value === 'string')
          swal('', messages.join('\n') || this.$t('validations.wentWrong'), 'error')
        }
      })
    },
    handleDelete () {
      attemptReferralCampaignDelete(this, this.campaignId, {
        buildDeactivateBody: () => this.payload(),
        onDeleted: () => { this.backButton() },
        onDeactivated: () => {
          this.model.active = false
          this.model.is_visible_for_users = false
        }
      })
    }
  }
}
</script>

<style scoped>
.referral-campaign-form {
  position: relative;
}

.referral-campaign-form__loader {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 2;
}

.referral-campaign-form .card-content .form-group {
  margin-bottom: 10px;
}

.referral-card-title {
  margin: 0 0 12px 0;
  font-weight: 600;
}

.referral-form-switches-row {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: center;
}

.referral-form-switch-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.referral-form-switch-item label {
  margin-bottom: 0;
}

.referral-campaign-form .card-content .row {
  margin-bottom: 4px;
}

.referral-section-heading {
  margin-bottom: 8px;
}

.referral-section-heading__title {
  margin: 0 0 3px 0;
  font-weight: 600;
}

.referral-section-heading__hint {
  margin: 0;
  line-height: 1.35;
}

.referral-form-inline-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 12px;
}

.referral-form-inline-toggle__label {
  margin-bottom: 0;
  font-weight: 600;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  line-height: 1.35;
}

.referral-section-heading__text {
  flex: 1;
  min-width: 0;
}

.referral-section-heading__switch {
  flex-shrink: 0;
}

.referral-reward-summary {
  margin: 14px 0 0;
  padding: 12px 14px;
  border-radius: 4px;
  background: #f7f9fc;
  border-left: 3px solid #51cbce;
  color: #333;
  line-height: 1.45;
}

.referral-reward-summary--disabled {
  border-left-color: #bbb;
  color: #888;
  background: #f5f5f5;
}

/* Bootstrap switch: remove stray bottom margin so flex vertical centering works */
.referral-form-inline-toggle >>> .bootstrap-switch-wrapper {
  margin-bottom: 0;
  vertical-align: middle;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Marketing/Referral/ReferralCampaignForm.vue